import type { ClaimWorkFindingHighlight, ClaimWorkItem } from '../store';

/**
 * THE presentation adapter over the canonical claim-work row (server
 * `claim_work_items`, PR smartscribe-server#4202).
 *
 * The same row powers list rows, queue/filter counts, saved-view counts,
 * batch-submit validation, AND the workspace detail's `work_summary` — so its
 * presentation vocabulary lives here ONCE. The claims worklist and the claim
 * workbench must read this module rather than keeping page-local label maps:
 * the list and the detail behave as one product, and a claim's state, counts,
 * and submission decision must not change just because someone opened it.
 */

/** `work_state` values the canonical view emits (claim_work_items.sql). */
export const WORK_STATE_LABELS: Record<string, string> = {
  draft: 'Draft',
  needs_correction: 'Needs correction',
  payment_posted: 'Payment posted',
  resolved: 'Work resolved',
  ready_to_submit: 'Ready to submit',
  submission_processing: 'Submission processing',
  with_payer: 'With payer',
};

export type WorkStateChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

export const WORK_STATE_COLORS: Record<string, WorkStateChipColor> = {
  draft: 'default',
  needs_correction: 'error',
  payment_posted: 'success',
  resolved: 'success',
  ready_to_submit: 'success',
  submission_processing: 'info',
  with_payer: 'info',
};

/**
 * The chip a claim work row wears in a list.
 *
 * A payer's verdict is the more specific truth, so a corrected claim that was
 * denied says "Denied", not "Needs correction" — the state that put it there
 * is less use than the reason. Lives here rather than in a page because two
 * surfaces render it (the Claims worklist and the Home queue cards) and a
 * claim must not change its name when it changes screens.
 */
const PAYER_OUTCOME_CHIP_LABELS = ['denied', 'rejected', 'reversed'];

/**
 * The payer paid some lines and not others, so money is still owed.
 * `payment_posted` covers this and a claim paid in full alike; the payer's
 * outcome is what tells them apart.
 */
function isPartiallyPaid(item: ClaimWorkItem): boolean {
  return item.work_state === 'payment_posted' && item.payer_outcome === 'partially_paid';
}

export function workStateChipLabel(item: ClaimWorkItem): string {
  if (isPartiallyPaid(item)) return 'Partially paid';
  return item.work_state === 'needs_correction' &&
    PAYER_OUTCOME_CHIP_LABELS.includes(item.payer_outcome)
    ? item.payer_outcome.charAt(0).toUpperCase() + item.payer_outcome.slice(1)
    : (WORK_STATE_LABELS[item.work_state] ?? item.work_state);
}

/** The chip's tone. A partial payment is never green: money is still owed. */
export function workStateChipColor(item: ClaimWorkItem): WorkStateChipColor {
  return isPartiallyPaid(item) ? 'warning' : (WORK_STATE_COLORS[item.work_state] ?? 'default');
}

/**
 * The line under a partial payment's chip when work is left on it. The row's
 * queue is the authority: a partially paid claim in Claims to fix needs
 * attention, whatever put it there. The ERA triage flag stands in only for a
 * row that carries no queue field at all.
 */
export function partialPaymentAttentionHint(item: ClaimWorkItem): string | null {
  if (!isPartiallyPaid(item)) return null;
  const needsAttention =
    item.queue === undefined ? item.era_needs_attention === true : item.queue === 'fix';
  return needsAttention ? 'Needs attention' : null;
}

/** Draft is the only outlined chip: nothing has happened to it yet. */
export function workStateChipVariant(item: ClaimWorkItem): 'filled' | 'outlined' {
  return item.work_state === 'draft' ? 'outlined' : 'filled';
}

/** Persisted decision hint only: list rows never fetch or guess paid-match eligibility. */
export function paidResolutionWorkHint(item: ClaimWorkItem): string | null {
  const outcome = item.payer_outcome === 'none' ? null : item.payer_outcome;
  if (item.paid_resolution_review_required || item.paid_resolution_state === 'invalidated') {
    return 'Prior resolution needs review';
  }
  if (item.paid_resolution_state === 'reopened') return 'Work reopened · prior resolution retained';
  if (item.paid_resolution_state !== 'resolved') return null;
  const reason = item.paid_resolution_reason === 'duplicate_of_paid_claim'
    ? 'Duplicate of paid claim'
    : item.paid_resolution_reason === 'resolved_by_paid_resubmission'
      ? 'Resolved by paid resubmission' : 'Issue resolved';
  const remaining = (item.paid_resolution_remaining_issue_count ?? 0) > 0;
  return [outcome ? `Payer outcome: ${outcome}` : null, reason, remaining ? 'Other work remains' : null].filter(Boolean).join(' · ');
}

/**
 * `submit_block_reason` is a closed enum on the canonical view; these are the
 * biller-facing fallbacks for each. Where the row carries the server's own
 * specific message (`submission_top_blocker` / `legacy_top_blocker`),
 * `submitBlockLabel` prefers it over these generics — "Billing provider NPI
 * is required" beats "Required claim information is missing".
 */
const SUBMIT_BLOCK_REASON_LABELS: Record<string, string> = {
  claim_status: 'This claim’s status doesn’t allow submission.',
  not_scrubbed: 'Checks haven’t run against this claim yet.',
  claim_validation: 'The claim failed validation checks.',
  submission_validation: 'Required claim information is missing.',
  certification_finding: 'Open blockers must be resolved first.',
  not_ready: 'This claim isn’t ready to submit.',
};

function submitBlockLabel(item: ClaimWorkItem): string | null {
  if (item.can_submit) return null;
  const reason = item.submit_block_reason ?? null;
  if (reason === 'submission_validation' && item.submission_top_blocker) {
    return item.submission_top_blocker;
  }
  if (reason === 'claim_validation' && item.legacy_top_blocker) {
    return item.legacy_top_blocker;
  }
  if (reason && SUBMIT_BLOCK_REASON_LABELS[reason]) return SUBMIT_BLOCK_REASON_LABELS[reason];
  /* An unknown (or absent) reason on a non-submittable row still refuses
     honestly — the server said no; the copy just can't say why. */
  return 'Submission isn’t available for this claim.';
}

export interface ClaimWorkSubmitModel {
  /** The server's decision, verbatim. Never re-derived client-side. */
  canSubmit: boolean;
  /** Raw `submit_block_reason` enum value, for logic/analytics. */
  blockReason: string | null;
  /** Biller-facing copy for the refusal — null exactly when `canSubmit`. */
  blockLabel: string | null;
}

export interface ClaimWorkSummaryModel {
  /** Raw `work_state` (kept for logic), plus its shared label/tone. */
  workState: string;
  stateLabel: string;
  stateTone: 'default' | 'success' | 'warning' | 'error' | 'info';
  claimLifecycle: string;
  /** Payer outcome, or null when the view says 'none'. */
  payerOutcome: string | null;
  /** current | stale | missing — the canonical row's freshness. */
  certificationFreshness: string;
  /** checking | complete | idle — persisted run state (survives reload). */
  certificationRunState: string;
  certificationDecision: string | null;
  /**
   * Server-authoritative counts (`active_*_count`). NEVER derived from
   * `findingHighlights.length` — highlights are a capped preview, not the
   * population.
   */
  counts: { findings: number; blockers: number; advisories: number };
  findingHighlights: ClaimWorkFindingHighlight[];
  submit: ClaimWorkSubmitModel;
  requiresCorrection: boolean;
}

/**
 * `work_summary` is null when the claim has no canonical row (e.g. a
 * superseded claim — the view excludes them). Callers treat null as "no
 * canonical answer", never as "no problems".
 */
export function deriveClaimWorkSummary(
  item: ClaimWorkItem | null | undefined,
): ClaimWorkSummaryModel | null {
  if (!item) return null;
  return {
    workState: item.work_state,
    stateLabel: WORK_STATE_LABELS[item.work_state] ?? item.work_state,
    stateTone: WORK_STATE_COLORS[item.work_state] ?? 'default',
    claimLifecycle: item.claim_lifecycle,
    payerOutcome: item.payer_outcome === 'none' ? null : item.payer_outcome,
    certificationFreshness: item.certification_freshness,
    certificationRunState: item.certification_run_state,
    certificationDecision: item.certification_decision ?? null,
    counts: {
      findings: item.active_finding_count ?? 0,
      blockers: item.active_blocker_count ?? 0,
      advisories: item.active_advisory_count ?? 0,
    },
    findingHighlights: item.finding_highlights ?? [],
    submit: {
      canSubmit: item.can_submit,
      blockReason: item.submit_block_reason ?? null,
      blockLabel: submitBlockLabel(item),
    },
    requiresCorrection: item.requires_correction,
  };
}
/**
 * Certification copy is PRE-FLIGHT advice (GDN-3242): "stale", "not checked
 * yet", "recheck before relying on it" all counsel an action that shapes
 * what gets (re)submitted. A claim is in a WORKABLE stage while the biller
 * can still do that shaping — drafting, ready to go, or pulled back into
 * work by a payer rejection. Once the snapshot is in transit or with the
 * payer, a recheck cannot change what they received, and "not checked yet"
 * on a paid claim reads as unfinished work.
 *
 * Derived as a positive, meaning-documented set (the GDN-3215 principle —
 * mirror the meaning, don't suppress known offenders): a work_state this
 * build has never heard of gets NO pre-flight advice until someone decides
 * it is workable. Genuine findings are NOT gated by this — payer-side
 * content renders wherever it exists.
 */
const WORKABLE_WORK_STATES = new Set(['draft', 'needs_correction', 'ready_to_submit']);

export function isWorkableClaimStage(workState: string): boolean {
  return WORKABLE_WORK_STATES.has(workState);
}

/**
 * The number to quote to the payer for a claim: its chain's reference. That is
 * the claim's own payer claim control number once the payer assigns one, or,
 * for a correction or void the payer has not answered, the number it was filed
 * against. The server derives it as `chain_payer_reference`; the claim's own
 * number stands in when a response carries no chain reference.
 */
export function payerReferenceOf(
  claim: Pick<ClaimWorkItem, 'chain_payer_reference' | 'payer_claim_control_number'>,
): string | null {
  return claim.chain_payer_reference?.trim() || claim.payer_claim_control_number?.trim() || null;
}
